// components/Blogs.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { addDoc, collection, deleteDoc, doc, getDocs, orderBy, query, setDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import { AnimatedPage } from '../components/AnimatedPage';
import { useUser, SignedIn, SignedOut, RedirectToSignUp } from "@clerk/clerk-react";
import { button } from 'framer-motion/client';

const PendingArticles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
//   const { user,isSignedIn } = useUser();
  const { user, isLoaded, isSignedIn } = useUser();

  useEffect(() => {
    // Only run once the user is fully loaded and signed in
    if (!isLoaded) return;

    if (isSignedIn) {
      const userId = user.id;
      const q = query(
        collection(db, "pending-articles"),
        orderBy("createdAt", "desc")
      );

      getDocs(q).then((querySnapshot) => {
        const userArticles = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setArticles(userArticles);
        setLoading(false);
      });
    } else {
      // If not signed in, stop loading and redirect
      setLoading(false);
    }
  }, [isLoaded, isSignedIn, user]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const getExcerpt = (content, maxLength = 150) => {
    const text = stripHtml(content);
    return text.length > maxLength 
      ? text.substring(0, maxLength) + '...' 
      : text;
  };
   const handleApprove = async (e,article) => {
    try {
      // 1. Save article to "articles" collection
      
      const docRef = await addDoc(collection(db, 'articles'), article);

      // 2. Delete it from "pendingArticles"
      await deleteDoc(doc(db, "pending-articles", article.id));

      // 3. Update local state
      setArticles(prev => prev.filter(a => a.id !== article.id));
    } catch (error) {
      console.error("Error approving article:", error);
    }
  };

  if (loading) {
    return (
      <AnimatedPage>
        <LoadingMessage>Loading articles...</LoadingMessage>
      </AnimatedPage>
    );
  }

const handleDelete = async (e, id) => {
  e.preventDefault();

  const confirmDelete = window.confirm("Are you sure you want to delete this article?");
  if (!confirmDelete) return;

  try {
    // Delete from Firestore
    await deleteDoc(doc(db, "articles", id));

    // Remove from local state so UI updates instantly
    setArticles((prev) => prev.filter((article) => article.id !== id));

    
  } catch (error) {
    console.error("Error deleting article:", error);
    alert("❌ Failed to delete article.");
  }
};
  return (
    <AnimatedPage>
      <ArticlesContainer>
        <PageHeader>
          <PageTitle>Pending Articles</PageTitle>
          {/* <NewArticleButton to="/write">
            + New Article
          </NewArticleButton> */}
        </PageHeader>

        {articles.length === 0 ? (
          <EmptyState>
            <h3>No articles yet</h3>
            <p>Start writing your first article to get started!</p>
            <NewArticleButton to="/write" style={{ marginTop: '16px', display: 'inline-block' }}>
              Create Your First Article
            </NewArticleButton>
          </EmptyState>
        ) : (
          <ArticlesGrid>
            {articles.map((article) => (
              <ArticleCard key={article.id}>
                <ArticleTitle>
                  <Link to={`/blog/${article.id}`}>
                    {article.title}
                  </Link>
                </ArticleTitle>
                <ArticleExcerpt>
                  {getExcerpt(article.content)}
                </ArticleExcerpt>
                <ArticleMeta>
                  <span>
                    {formatDate(article.createdAt)} • {article.wordCount} words
                  </span>
                  <div style={{ gap:10, display: 'flex', alignItems: 'center' }}>
                   <DeleteButton onClick={e=>handleDelete(e,article.id)}>
                  Delete
                  </DeleteButton>
                    <DeleteButton onClick={e=>handleApprove(e,article)}>
                    Approve
                  </DeleteButton>
                  <ReadMoreLink to={`/pending-blog/${article.id}`}>
                    Read more →
                  </ReadMoreLink>
                  </div>
                </ArticleMeta>
              </ArticleCard>
            ))}
          </ArticlesGrid>
        )}
      </ArticlesContainer>
    </AnimatedPage>
  );
};

export default PendingArticles;

const ArticlesContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 60px 20px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
`;

const PageTitle = styled.h1`
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 36px;
  font-weight: 600;
  color: #034C53;
  margin: 0;
`;

const NewArticleButton = styled(Link)`
  background: #66BFBF;
  color: white;
  padding: 12px 24px;
  border-radius: 6px;
  text-decoration: none;
  font-family: 'Clash Grotesk', sans-serif;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: #5ab0b0;
    transform: translateY(-1px);
  }
`;

const ArticlesGrid = styled.div`
  display: grid;
  gap: 24px;
`;

const ArticleCard = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 24px;
  transition: all 0.2s ease;
  background: white;

  &:hover {
    border-color: #66BFBF;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const ArticleTitle = styled.h2`
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 24px;
  font-weight: 500;
  color: #034C53;
  margin: 0 0 12px 0;
  
  a {
    color: inherit;
    text-decoration: none;
    
    &:hover {
      color: #66BFBF;
    }
  }
`;

const ArticleExcerpt = styled.p`
  font-family: 'Clash Grotesk', sans-serif;
  color: #184A2C;
  line-height: 1.6;
  margin-bottom: 16px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ArticleMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 14px;
  color: #6c757d;
`;

const DeleteButton = styled(button)`
  color: #f02424ff;
  text-decoration: none;
  font-weight: 500;
  border: none;
  background: none;
  cursor: pointer;
  

`;

const ReadMoreLink = styled(Link)`
  color: #66BFBF;
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-family: 'Clash Grotesk', sans-serif;
  color: #034C53;
  font-size: 18px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  font-family: 'Clash Grotesk', sans-serif;
  color: #6c757d;
  
  h3 {
    color: #034C53;
    margin-bottom: 16px;
  }
`;
